import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, getVoterId } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { catLabel, regionLabel, t } from "@/lib/i18n";
import IntensityMeter from "@/components/IntensityMeter";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, SpeakerHigh } from "@phosphor-icons/react";

export default function WordDetailPage() {
  const { id } = useParams();
  const { lang } = useApp();
  const [word, setWord] = useState(null);
  const [voting, setVoting] = useState(false);
  const [voted, setVoted] = useState(
    () => localStorage.getItem(`iswear_voted_${id}`) || null,
  );

  useEffect(() => {
    api.get(`/words/${id}`).then((r) => setWord(r.data));
  }, [id]);

  async function castVote(value) {
    setVoting(true);
    try {
      const r = await api.post(`/words/${id}/vote`, {
        intensity: value,
        voter_id: getVoterId(),
      });
      setWord(r.data);
      localStorage.setItem(`iswear_voted_${id}`, String(value));
      setVoted(String(value));
      toast.success(t("vote_thanks", lang));
    } finally {
      setVoting(false);
    }
  }

  if (!word) {
    return (
      <main className="max-w-4xl mx-auto px-6 py-24 text-center text-gray-500">
        {t("loading", lang)}
      </main>
    );
  }

  const tr = word.translations || {};
  const def = word.definition || {};
  const metaDesc =
    def[lang] || def.no || def.en || def.es || def.pl || "Norsk banneord.";
  const ogImage = `${process.env.REACT_APP_BACKEND_URL}/api/og/${word.id}.png`;

  return (
    <main data-testid="word-detail-page" className="max-w-4xl mx-auto px-6 lg:px-8 py-12 sm:py-16">
      <Seo
        title={`${word.word} — I Swear · norsk banneord`}
        description={metaDesc.slice(0, 280)}
        image={ogImage}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "DefinedTerm",
          name: word.word,
          description: def.no || def.en || def.es || def.pl || "",
          inDefinedTermSet: {
            "@type": "DefinedTermSet",
            name:
              word.source_lang === "es"
                ? "I Swear — insultos españoles"
                : "I Swear — norsk banneordbok",
            url: word.source_lang === "es" ? "https://iswear.no/es" : "https://iswear.no/",
          },
        }}
      />
      <Link
        to={word.source_lang === "es" ? "/es" : "/"}
        data-testid="back-link"
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-10"
      >
        <ArrowLeft size={14} /> {t("back", lang)}
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <span className="inline-block px-3 py-1 rounded-full bg-[#F3F1ED] text-[10px] tracking-eyebrow font-medium text-gray-700">
          {catLabel(word.category, lang)}
        </span>
        {word.region && (
          <span className="text-[10px] tracking-eyebrow font-mono-code text-gray-500">
            · {regionLabel(word.region, lang)}
          </span>
        )}
      </div>

      <h1
        data-testid="word-title"
        className="font-serif-display font-bold tracking-tighter text-gray-900 text-6xl sm:text-8xl lg:text-9xl leading-none"
      >
        {word.word}
      </h1>

      <div className="mt-4 flex items-center gap-4">
        <button
          data-testid="tts-play-btn"
          onClick={() => {
            const audio = new Audio(
              `${process.env.REACT_APP_BACKEND_URL}/api/words/${word.id}/tts.mp3`,
            );
            audio.play().catch(() => toast.error("Audio error"));
          }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-300 hover:border-[#E85D04] hover:text-[#E85D04] text-xs tracking-eyebrow text-gray-600 transition-colors"
        >
          <SpeakerHigh size={14} weight="fill" /> {lang === "no" ? "Lytt" : lang === "es" ? "Escuchar" : lang === "pl" ? "Posłuchaj" : "Listen"}
        </button>
        <a
          data-testid="og-card-link"
          href={`${process.env.REACT_APP_BACKEND_URL}/api/og/${word.id}.png`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-xs tracking-eyebrow text-gray-500 hover:text-[#E85D04] transition-colors"
        >
          ↗ {lang === "no" ? "Delbart kort" : lang === "es" ? "Tarjeta compartible" : lang === "pl" ? "Karta do udostępniania" : "Shareable card"}
        </a>
      </div>

      {(() => {
        const def = word.definition || {};
        const ex = word.example || {};
        const definitionText = def[lang] || def.no || def.es || def.en || def.pl || "";
        const exampleText = ex[lang] || ex.no || ex.es || ex.en || ex.pl || "";
        return (
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7 space-y-10">
              <section>
                <h2 className="text-[11px] tracking-eyebrow text-gray-500 mb-3">
                  {t("definition_label", lang)}
                </h2>
                <p className="font-serif-display italic text-2xl sm:text-3xl text-gray-800 leading-snug">
                  {definitionText}
                </p>
              </section>

              <section>
                <h2 className="text-[11px] tracking-eyebrow text-gray-500 mb-3">
                  {t("example_label", lang)}
                </h2>
                <blockquote className="border-l-2 border-[#E85D04] pl-5 text-lg text-gray-700 leading-relaxed">
                  {exampleText}
                </blockquote>
              </section>

          <section>
            <h2 className="text-[11px] tracking-eyebrow text-gray-500 mb-4">
              {t("translations_label", lang)}
            </h2>
            <div className="space-y-4">
              {[
                { code: "no", label: "Norsk" },
                { code: "es", label: "Español" },
                { code: "en", label: "English" },
                { code: "pl", label: "Polski" },
              ].map((l) => (
                <div
                  key={l.code}
                  data-testid={`translation-${l.code}`}
                  className={`rounded-xl border p-5 transition-colors ${
                    lang === l.code
                      ? "border-[#E85D04] bg-white"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] tracking-eyebrow font-medium text-gray-500">
                      {l.label}
                    </span>
                    <span className="font-mono-code text-[10px] text-gray-400 uppercase">
                      {l.code}
                    </span>
                  </div>
                  <p className="text-base text-gray-800 leading-relaxed">
                    {tr[l.code] || "—"}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="lg:col-span-5">
          <div className="sticky top-24 bg-white border border-gray-200 rounded-2xl p-7">
            <h2 className="text-[11px] tracking-eyebrow text-gray-500">
              {t("intensity_label", lang)}
            </h2>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-serif-display text-6xl font-bold text-gray-900">
                {word.intensity_avg ? word.intensity_avg.toFixed(1) : "—"}
              </span>
              <span className="text-sm text-gray-500">/ 5</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {word.intensity_votes_count} {t("votes", lang)}
            </p>
            <div className="mt-5">
              <IntensityMeter value={word.intensity_avg || 0} size="lg" />
            </div>

            <div className="mt-8">
              <h3 className="text-[11px] tracking-eyebrow text-gray-500 mb-3">
                {t("vote_prompt", lang)}
              </h3>
              <div className="grid grid-cols-5 gap-2">
                {[1, 2, 3, 4, 5].map((v) => (
                  <button
                    key={v}
                    data-testid={`vote-btn-${v}`}
                    onClick={() => castVote(v)}
                    disabled={voting}
                    className={`py-3 rounded-lg border text-sm font-medium transition-all ${
                      voted === String(v)
                        ? "border-[#E85D04] bg-[#E85D04] text-white"
                        : "border-gray-200 bg-white text-gray-700 hover:border-gray-400"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
              <div className="flex justify-between mt-2 text-[10px] tracking-eyebrow text-gray-400">
                <span>{t("intensity_1", lang)}</span>
                <span>{t("intensity_5", lang)}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
        );
      })()}
    </main>
  );
}
